/**
 * The pass: whole courses, across every station cooking them.
 *
 * A prep rail answers "what have I got on", which is the right question at a
 * grill and the wrong one at the pass. The person sending a table's food out
 * needs to know the mains are three-quarters done and which quarter is
 * missing, and no single station's screen can say that.
 *
 * So a docket here is a course rather than a ticket, and it carries a line per
 * station with a mark against the ones that are up. The card is the same
 * object as a rail docket — same clock, same urgency rules, same weight
 * escalation — because a kitchen that reads one should not have to learn the
 * other.
 *
 * Away is the only action. It is deliberately not a bump: a cook bumps to say
 * their part is done, and away says the plates are on a tray going out, which
 * is a fact about all of it. It clears the course from every rail in the
 * kitchen, so nobody is left tidying a screen after the food has gone.
 *
 * A course stays here once everything is up rather than vanishing at that
 * moment — "all of it is ready, send it" is the state this screen exists to
 * act on, and it would otherwise last no time at all.
 */

import {
  formatElapsed,
  urgencyAt,
  useCallAway,
  useTicking,
  type PassDocket,
} from '@restaurant/api-client';
import { Button } from '@restaurant/ui';

import { TabNote } from './tab-note';

export function PassDocketCard({
  docket,
  stationId,
}: {
  docket: PassDocket;
  stationId: string;
}) {
  const away = useCallAway();
  const elapsed = useTicking(docket.elapsed_seconds);
  const urgency = docket.fired_at ? urgencyAt(elapsed, docket.sla_seconds) : 'on_time';
  const where = docket.table_label
    ? `Table ${docket.table_label}`
    : `Order ${docket.order_number}`;
  // By name, once each: a station with an addition still cooking holds two
  // dockets here, and "waiting on Grill and Grill" says nothing more.
  const waiting = [
    ...new Set(
      docket.stations
        .filter((station) => station.state !== 'bumped')
        .map((station) => station.station_name),
    ),
  ];

  return (
    <article
      className={`dk px${urgency ? ` dk--${urgency}` : ''}${docket.ready ? ' px--ready' : ''}`}
      aria-label={
        `${where}, course ${docket.course_no}, ` +
        (docket.ready ? 'all stations ready' : `waiting on ${waiting.join(', ')}`)
      }
    >
      <header className="dk__head">
        <span className="dk__where">{where}</span>
        {docket.from_location_name && (
          <span className="dk__for">For {docket.from_location_name}</span>
        )}
        <span className="dk__clock">{formatElapsed(elapsed)}</span>
      </header>

      {/* Said in words as well as shown, because the ready state is the one a
          runner acts on and hue is the channel a steamed screen loses first. */}
      <p className="px__state">
        {docket.ready ? 'All up' : `Waiting on ${waiting.join(' and ')}`}
      </p>

      <TabNote note={docket.order_note} />

      <ul className="px__stations">
        {docket.stations.map((station) => (
          <li
            key={station.ticket_id}
            className={`px__station${station.state === 'bumped' ? ' px__station--done' : ''}`}
          >
            <span className="px__station-name">
              {station.station_name}
              {/* Another site's kitchen is cooking this share — it arrives
                  rather than being plated here, so the pass knows where to
                  look for it. */}
              {station.kitchen_name && (
                <span className="px__station-kitchen"> at {station.kitchen_name}</span>
              )}
              {/* Dishes added after this station bumped the course: the pass
                  sees the grill's mains are up and its late side is not. */}
              {station.follow_up && <span className="px__station-added">added</span>}
              <span className="px__station-state">
                {station.state === 'bumped' ? 'up' : 'cooking'}
              </span>
            </span>
            <ul className="px__items">
              {station.items.map((item) => (
                <li key={item.id} className="px__item">
                  {/* Through Number, as the rail does: the wire carries
                      "2.000" and a cook reads 2. */}
                  <span className="px__qty">{Number(item.qty)}</span>
                  <span>{item.name}</span>
                  {item.modifiers.length > 0 && (
                    <span className="px__mods">{item.modifiers.join(', ')}</span>
                  )}
                  {item.note && <span className="px__note">{item.note}</span>}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <Button
        variant="primary"
        size="lg"
        block
        loading={away.isPending}
        onClick={() =>
          away.mutate({
            stationId,
            orderId: docket.order_id,
            courseNo: docket.course_no,
          })
        }
      >
        Away
      </Button>
    </article>
  );
}
